import { supabase } from '../utils/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface ActivityLog {
  id: string;
  user_id: string;
  activity_type: 'login' | 'logout' | 'access';
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface RecentActivityLog extends ActivityLog {
  user_name: string;
  user_email: string;
  user_role: string;
}

export interface ActivityStats {
  total_logins: number;
  unique_users: number;
  today_logins: number;
  this_week_logins: number;
  this_month_logins: number;
}

class ActivityService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    // ✅ Use Supabase's session method instead of manual localStorage parsing
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error || !session) {
      console.warn('No active session found');
      return { 'Content-Type': 'application/json' };
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    };
  }

  /**
   * Log user activity
   */
  async logActivity(
    activityType: 'login' | 'logout' | 'access',
    metadata?: Record<string, any>
  ): Promise<ActivityLog | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/activity/log`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          activity_type: activityType,
          metadata: metadata || {},
        }),
      });

      if (!response.ok) {
        console.error('Failed to log activity');
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error logging activity:', error);
      return null;
    }
  }

  /**
   * Get recent user activity (admin only)
   */
  async getRecentActivity(limit: number = 100, offset: number = 0): Promise<{
    data: RecentActivityLog[];
    total: number;
  } | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_URL}/activity/recent?limit=${limit}&offset=${offset}`,
        {
          headers,
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch recent activity');
        return null;
      }

      const result = await response.json();
      return {
        data: result.data,
        total: result.total,
      };
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      return null;
    }
  }

  /**
   * Get activity statistics (admin only)
   */
  async getActivityStats(): Promise<ActivityStats | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/activity/stats`, {
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch activity stats');
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      return null;
    }
  }

  /**
   * Get activity for a specific user
   */
  async getUserActivity(userId: string, limit: number = 50): Promise<ActivityLog[] | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_URL}/activity/user/${userId}?limit=${limit}`,
        {
          headers,
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch user activity');
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return null;
    }
  }
}

export const activityService = new ActivityService();
